import React, { FC, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IdEnums, ShortCodeEnums, CoreUtils } from '@nhl/nhl-breakaway-ui';
import Headshot from '@nhl/nhl-react-ui/lib/Headshot';
import headshotTypes from '@nhl/nhl-react-ui/lib/Headshot/headshotTypes';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import CommonMessages from '~/core/commonMessages';
import { DasherBoardText } from '~/core/enums';
import { getDataLayer } from '~/core/googleAnalyticsUtils';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { getPlayerHref, parseValueFromLocalizedAttribute, useGoalDescriptors } from '~/core/utils';
import GoalDescriptors from '~/views/components/goalDescriptors/GoalDescriptors';
import VideoClipPlayer from '~/views/components/videoClipPlayer/VideoClipPlayer';

import { PptReplayTeam as Team } from '../../../../../pptReplayEmbed/types';
import { GameCenterTeam, Goal } from '../../../../types';
import { FormatGoalScorer, FormattedPlayer } from '../../../../utils';
import PPTReplay from '../../../pptReplay/PPTReplay';

import { formatGameScore } from './utils';

import {
    AssistInformation,
    Assists,
    EntityMarker,
    GoalInfoContainer,
    GoalScorerContainer,
    GoalSimulationExpander,
    InfoContainer,
    InfoData,
    PlayerAndInfoContainer,
    PlayerInfo,
    PlayerInfoContainer,
    PlayerName,
    ShootoutTeamDisplay,
    TopRow,
} from './styles';

type Props = {
    gameId: number;
    homeTeam: GameCenterTeam | Team;
    awayTeam: GameCenterTeam | Team;
    goal: Goal;
    logoUrl: string;
    isLarge: boolean;
    isLimitedScoring: boolean;
    isWebview: boolean;
    gameState: ShortCodeEnums.GameState | null;
    gameType: IdEnums.GameType;
    period?: number;
    eventId?: number;
    isPptEmbed?: boolean;
};

const GoalScorer: FC<Props> = ({
    gameId,
    homeTeam,
    awayTeam,
    goal,
    logoUrl,
    isLarge,
    isLimitedScoring,
    isWebview,
    period,
    eventId,
    gameState,
    gameType,
    isPptEmbed,
}) => {
    const theme = useTheme();
    const intl = useIntl();
    const isEnglish =
        intl.locale === ShortCodeEnums.Language.English || intl.locale === ShortCodeEnums.Language.EnglishUS;

    const [showGoalSimulation, setShowGoalSimulation] = useState(false);

    const highlightClip = useOptionForLocale({
        [ShortCodeEnums.Language.English]: goal.highlightClip,
        [ShortCodeEnums.Language.French]: goal.highlightClipFr,
    });

    const goalDescriptors = useGoalDescriptors(goal.strength, goal.goalModifier);
    const leadingTeamAbbrev = parseValueFromLocalizedAttribute(goal.leadingTeamAbbrev);
    const shouldShowPptPlay = !isPptEmbed && goal.pptReplayUrl !== undefined;
    const isShootoutGoal = goal.periodDescriptor?.periodType === ShortCodeEnums.PeriodType.shootout;

    const assists =
        goal.assists.length > 0
            ? intl.formatList(
                  goal.assists.map(
                      (assist) => `${parseValueFromLocalizedAttribute(assist.name)} (${assist.assistsToDate})`
                  ),
                  { type: 'unit' }
              )
            : intl.formatMessage(CommonMessages.statsDescriptions.long.unassisted);

    const toggleGoalSimulation = () => {
        // if the goal simulation isn't currently shown, and we're about to show it we fire a GA event
        if (!showGoalSimulation) {
            getDataLayer().push({
                event: 'goal_simulation_open_click',
                game_id: gameId,
                game_state: gameState,
            });
        }

        setShowGoalSimulation(!showGoalSimulation);
    };

    const goalScorer = <FormattedPlayer player={goal} />;

    return (
        <GoalScorerContainer $isPptEmbed={isPptEmbed}>
            <PlayerAndInfoContainer>
                <TopRow>
                    <PlayerInfoContainer>
                        <Headshot
                            type={headshotTypes.SKATER}
                            urlOverride={goal.headshot}
                            size={isLarge ? 64 : 48}
                            backgroundColor={
                                goal.teamAbbrev.default
                                    ? theme.Team[goal.teamAbbrev.default]?.gamecenter
                                    : theme.Surface.secondary
                            }
                        />
                        <PlayerInfo>
                            <PlayerName href={getPlayerHref(goal.playerId, isWebview)} rel="noreferrer">
                                {isPptEmbed && (
                                    <>
                                        {isShootoutGoal && (
                                            <ShootoutTeamDisplay
                                                logoUrlOverride={logoUrl}
                                                team={{ triCode: parseValueFromLocalizedAttribute(goal.teamAbbrev) }}
                                                displayName={false}
                                                useInTable
                                            />
                                        )}
                                        <EntityMarker
                                            $isHome={goal.isHome}
                                            $teamAbbrev={goal.isHome ? homeTeam.abbrev : awayTeam.abbrev}
                                        >
                                            {goal.sweaterNumber}
                                        </EntityMarker>
                                    </>
                                )}

                                {isPptEmbed && isShootoutGoal ? (
                                    <>
                                        {parseValueFromLocalizedAttribute(goal.firstName)}
                                        &nbsp;
                                        {parseValueFromLocalizedAttribute(goal.lastName)}
                                    </>
                                ) : (
                                    <FormatGoalScorer
                                        goalScorer={goalScorer}
                                        isShootoutEvent={isShootoutGoal}
                                        totalGoals={goal.goalsToDate}
                                    />
                                )}
                                <GoalDescriptors goalDescriptors={goalDescriptors} />
                            </PlayerName>

                            {!isShootoutGoal && (
                                <AssistInformation>
                                    <TeamDisplay
                                        logoUrlOverride={logoUrl}
                                        team={{ triCode: parseValueFromLocalizedAttribute(goal.teamAbbrev) }}
                                        displayName={false}
                                        useInTable
                                    />
                                    <Assists title={assists}>{assists}</Assists>
                                </AssistInformation>
                            )}
                        </PlayerInfo>
                    </PlayerInfoContainer>
                    {!isLarge && (
                        <VideoClipPlayer homeTeam={homeTeam} awayTeam={awayTeam} goal={goal} gameState={gameState} />
                    )}
                </TopRow>
                <GoalInfoContainer $noHighlightClip={!highlightClip}>
                    <InfoContainer $isEnglish={isEnglish}>
                        <InfoData $isEnglish={isEnglish}>
                            {formatGameScore(goal.homeScore, goal.awayScore, leadingTeamAbbrev)}
                        </InfoData>
                        <FormattedMessage {...CommonMessages.statsDescriptions.long.score} />
                    </InfoContainer>
                    <InfoContainer $isEnglish={isEnglish}>
                        <InfoData $isEnglish={isEnglish}>
                            {!isShootoutGoal && goal.timeInPeriod}
                            {isPptEmbed &&
                                goal.periodDescriptor &&
                                gameType &&
                                ` (${CoreUtils.getPeriodDisplayFromDescriptor(intl, goal.periodDescriptor, gameType)})`}
                        </InfoData>
                        <FormattedMessage {...CommonMessages.tableHeaders.long.gameEventTime} />
                    </InfoContainer>
                    {!isLimitedScoring && (
                        <InfoContainer $isEnglish={isEnglish}>
                            <InfoData $isEnglish={isEnglish}>
                                {CommonMessages.eventDescriptions[goal.shotType] ? (
                                    <CoreUtils.SafeFormattedMessage
                                        message={CommonMessages.eventDescriptions[goal.shotType]}
                                    />
                                ) : (
                                    goal.shotType || (
                                        <FormattedMessage {...CommonMessages.statsDescriptions.noStatIndicator} />
                                    )
                                )}
                            </InfoData>
                            <FormattedMessage {...CommonMessages.statsDescriptions.long.shotType} />
                        </InfoContainer>
                    )}
                    {isLarge && (
                        <VideoClipPlayer homeTeam={homeTeam} awayTeam={awayTeam} goal={goal} gameState={gameState} />
                    )}
                </GoalInfoContainer>
            </PlayerAndInfoContainer>
            {shouldShowPptPlay && (
                <>
                    {showGoalSimulation && goal.pptReplayUrl && (
                        <PPTReplay
                            gameId={gameId}
                            gameState={gameState}
                            homeTeam={homeTeam}
                            awayTeam={awayTeam}
                            homeTeamDefendingSide={goal.homeTeamDefendingSide}
                            pptReplayUrl={goal.pptReplayUrl}
                            goalScorerId={goal.playerId}
                            dasherBoardText={DasherBoardText.GOAL}
                            period={period}
                            eventId={eventId}
                        />
                    )}
                    <GoalSimulationExpander onClick={toggleGoalSimulation}>
                        <FormattedMessage
                            defaultMessage="EDGE | Goal Visualizer"
                            description="Title for the player and puck tracking goal simulation replay"
                        />
                        {showGoalSimulation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </GoalSimulationExpander>
                </>
            )}
        </GoalScorerContainer>
    );
};

export default GoalScorer;
